import type {FeedbackReaction} from '@Components/feedback-toast/feedback-toast.types';
import type {EmailCampaignAiRedesignSource} from '@Libraries/ai-email-campaign-content-library';
import {EmailCampaignAiRegenerateImageSource, EmailCampaignAiRewriteSource} from '@Libraries/ai-email-campaign-content-library';
import {getAiContentErrorMessage} from '@Libraries/ai-email-campaign-content-library';
import type {ErrorType} from '@Components/ai-text-prompt-box/ai-text-prompt-box.types';
import {AiTextErrorTypes} from '@Components/ai-text-prompt-box/ai-text-prompt-box.types';
import {AiRedesignPromptBoxIssue} from '@Libraries/ai-redesign-prompt-box-library';
import {getErrorMessageFromCode} from '@Libraries/ai-text-prompt-box-library';
import type {Editor} from 'grapesjs';
import type {EditableComponentsMap, SelectedPromptOptionValues} from '@Components/ai-resdesign-prompt-box/ai-redesign-prompt-box.types';
import {RedesignIssueType} from '@Components/ai-resdesign-prompt-box/ai-redesign-prompt-box.types';
import {EditableComponent} from '@Components/ai-resdesign-prompt-box/ai-redesign-prompt-box.types';
import {isUserLoggedIn, isUserPremium} from '@Libraries/user.library';

export function getRedesignIssueIconFromCode(code: AiTextErrorTypes): string {
  switch (code) {
    case AiTextErrorTypes.LowCredits:
      return 'icon-credits';
    default:
      return 'icon-exclamation-triangle';
  }
}

export function getRedesignIssueTypeFromCode(code: AiTextErrorTypes): RedesignIssueType {
  switch (code) {
    case AiTextErrorTypes.LowCredits:
      return RedesignIssueType.WARNING;
    default:
      return RedesignIssueType.ERROR;
  }
}

export async function fetchAiRedesignRecentPrompt(): Promise<string | null> {
  try {
    if (!isUserLoggedIn() || !isUserPremium()) {
      return null;
    }

    return (await window.PMW.readLocal('emailcampaignaicontent/getAiRedesignRecentPrompt')) as string | null;
  } catch {
    return null;
  }
}

async function rewriteTextComponentsForAiRedesign(editor: Editor, prompt: string, selectedPromptOptionValues: SelectedPromptOptionValues, contentKey: string): Promise<void> {
  try {
    editor.runCommand(window.PMW.emails.commands.TOGGLE_LOADING_OVERLAY_FOR_EMAIL, {show: true});
    await editor.runCommand(window.PMW.emails.commands.AI_REWRITE_EMAIL_CAMPAIGN_TEXT_COMPONENTS_CONTENT, {
      prompt,
      selectedPromptOptionValues,
      usageSource: EmailCampaignAiRewriteSource.EMAIL_MAKER_AI_REDESIGN,
      contentKey,
    });
  } finally {
    editor.runCommand(window.PMW.emails.commands.TOGGLE_LOADING_OVERLAY_FOR_EMAIL, {show: false});
  }
}

async function checkIfUserHasCreditsForAiRedesign(editableComponentsMap: EditableComponentsMap): Promise<void> {
  await window.PMW.readLocal('emailcampaignaicontent/checkUserHasCreditsForAiRedesign', {
    editableComponentsMap,
  });
}

export async function applyAiRedesignChanges(
  editor: Editor,
  editableComponentsMap: EditableComponentsMap,
  prompt: string,
  selectedPromptOptionValues: SelectedPromptOptionValues,
  contentKey: string
): Promise<void> {
  if (editableComponentsMap[EditableComponent.TEXT]) {
    editor.runCommand(window.PMW.emails.commands.TOGGLE_LOADING_OVERLAY_FOR_EMAIL, {show: true});
  }

  await checkIfUserHasCreditsForAiRedesign(editableComponentsMap);

  if (editableComponentsMap[EditableComponent.TEXT]) {
    await rewriteTextComponentsForAiRedesign(editor, prompt, selectedPromptOptionValues, contentKey);
  }

  if (editableComponentsMap[EditableComponent.IMAGE]) {
    await editor.runCommand(window.PMW.emails.commands.AI_REGENERATE_EMAIL_CAMPAIGN_IMAGES, {
      prompt,
      selectedPromptOptionValues,
      usageSource: EmailCampaignAiRegenerateImageSource.EMAIL_MAKER_AI_REDESIGN,
      contentKey,
    });
  }
}

export function handleAiRedesignError(error: ErrorType): void {
  const message = getAiContentErrorMessage(error);
  if (!message) {
    throw new AiRedesignPromptBoxIssue(
      getErrorMessageFromCode(AiTextErrorTypes.GeneralError),
      getRedesignIssueIconFromCode(AiTextErrorTypes.GeneralError),
      getRedesignIssueTypeFromCode(AiTextErrorTypes.GeneralError)
    );
  }

  const {code} = error;
  const icon = getRedesignIssueIconFromCode(code as AiTextErrorTypes);
  const type = getRedesignIssueTypeFromCode(code as AiTextErrorTypes);

  throw new AiRedesignPromptBoxIssue(message, icon, type);
}

export async function saveAiRedesignGenerationResults(prompt: string, usageSource: EmailCampaignAiRedesignSource, contentKey: string): Promise<string | null> {
  return (await window.PMW.writeLocal('emailcampaignaicontent/saveAiRedesignGenerationResults', {
    prompt,
    usageSource: usageSource,
    contentKey,
  })) as string | null;
}

export async function submitAiRedesignFeedbackReaction(redesignHashedId: string, reaction: FeedbackReaction): Promise<void> {
  await window.PMW.writeLocal('emailcampaignaicontent/recordAiRedesignFeedbackReaction', {
    id: redesignHashedId,
    reaction: reaction,
  });
}

async function submitAiRedesignFeedbackComment(redesignHashedId: string, feedbackText: string): Promise<void> {
  await window.PMW.writeLocal('emailcampaignaicontent/recordAiRedesignFeedbackComment', {
    id: redesignHashedId,
    comment: feedbackText,
  });
}

export function handleAiRedesignFeedback(redesignHashedId: string): void {
  const submitFeedbackReaction = async (reaction: FeedbackReaction): Promise<void> => {
    await submitAiRedesignFeedbackReaction(redesignHashedId, reaction);
  };

  const submitFeedbackTextInput = async (feedbackText: string): Promise<void> => {
    await submitAiRedesignFeedbackComment(redesignHashedId, feedbackText);
  };

  window.PMW.showFeedbackToast({
    onFeedbackReaction: submitFeedbackReaction,
    onFeedbackTextInput: submitFeedbackTextInput,
    reactionPageHeaderTitle: window.i18next.t('pmwjs_ai_just_worked_its_magic'),
    reactionPageHeaderIcon: 'icon-check',
    feedbackInputHeaderTitle: window.i18next.t('pmwjs_help_us_improve'),
    feedbackInputHeaderIcon: 'icon-message',
    feedbackInputPlaceholder: window.i18next.t('pmwjs_explain_your_experience_ellipsis'),
    postSubmissionText: window.i18next.t('pmwjs_thank_you_for_your_feedback'),
  });
}
